import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Form,
  Input,
  Button,
  Card,
  Row,
  Col,
  Collapse,
  Table,
  Divider,
  message,
  Spin,
  Typography,
  Space,
} from "antd";
import { ExportOutlined } from "@ant-design/icons";
import "./index.css";
import { getEndorsementCalculationDetails, getEndorsementCalculationUploadedFile, getEndorsementCalculationDataExport } from "../../../Action/EndorsementcalcApi";
import moment from "moment";

const { Title } = Typography;

const renderNonZero = (text) => {
  if (text && parseFloat(text) !== 0) {
    return typeof text === "number" ? text.toFixed(2) : text;
  }
  return "";
};

const baseColumns = [
  { title: "Age Group", dataIndex: "age_group", key: "ageGroup" },
  { title: "Sum Insured", dataIndex: "sum_insured", key: "sumInsured" },
  { title: "Annual Premium", dataIndex: "annual_premium", key: "annualPremium", render: (text) => text || 'N/A' },
  { title: "Premium Per Day", dataIndex: "premium_per_day", key: "premiumPerDay", render: (text) => text || 'N/A' },
  { title: "Total Days", dataIndex: "total_days", key: "totalDays", render: renderNonZero },
  { title: "Member Count", dataIndex: "member_count", key: "memberCount", render: renderNonZero },
];

const additionColumns = [
  ...baseColumns,
  { title: "Addition Premium", dataIndex: "premium_value", key: "additionPremium", render: renderNonZero },
];
const deletionColumns = [
  ...baseColumns,
  { title: "Deletion Premium", dataIndex: "premium_value", key: "deletionPremium", render: renderNonZero },
];

const ViewEndorsement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [endorsementData, setEndorsementData] = useState(null);
  const [additionTableData, setAdditionTableData] = useState([]);
  const [deletionTableData, setDeletionTableData] = useState([]);

  const processAndSetData = (reconstructedData) => {
    if (!reconstructedData) {
      message.error("Failed to process endorsement data.");
      navigate("/admin/endorsement_list");
      return;
    }

    setEndorsementData(reconstructedData);

    const inputDateFormat = "DD-MM-YYYY";

    form.setFieldsValue({
      ...reconstructedData,
      policy_start_date: moment(reconstructedData.policy_start_date, inputDateFormat).format("DD-MM-YYYY"),
      policy_end_date: moment(reconstructedData.policy_end_date, inputDateFormat).format("DD-MM-YYYY"),
      total_addition_premium: reconstructedData.total_addition_premium?.toFixed(2),
      total_deletion_premium: reconstructedData.total_deletion_premium?.toFixed(2),
      net_premium: reconstructedData.net_premium?.toFixed(2),
    });

    const additions = reconstructedData.data
      .filter((item) => item.premium_type_id === 1)
      .map((item, index) => ({ ...item, key: `add-${index}` }));

    const deletions = reconstructedData.data
      .filter((item) => item.premium_type_id === 2)
      .map((item, index) => ({ ...item, key: `del-${index}` }));

    setAdditionTableData(additions);
    setDeletionTableData(deletions);
    
    document.title = `View: ${reconstructedData.endorsement_title || 'Endorsement'}`;
  };

  useEffect(() => {
    setLoading(true);
    const endorsementId = location.state?.endorsementId;

    if (endorsementId) {
      dispatch(getEndorsementCalculationDetails(endorsementId))
        .then((response) => {
          const endorsementDetails = response?.endorsementDetails;
          
          if (endorsementDetails && Array.isArray(endorsementDetails.data) && endorsementDetails.data.length > 0 && endorsementDetails.premium_data) {
            
            const mainDetails = endorsementDetails.data[0];
            const additionData = endorsementDetails.premium_data.addition_premium || [];
            const deletionData = endorsementDetails.premium_data.deletion_premium || [];
            const combinedPremiumData = [...additionData, ...deletionData];

            const uploadId = (additionData[0]?.ref_endorsment_calculation_data_upload_id ||
                              deletionData[0]?.ref_endorsment_calculation_data_upload_id);

            const reconstructedData = {
                ...mainDetails,
                data: combinedPremiumData,
                ref_endorsement_calculation_data_upload_id: uploadId,
            };
            
            processAndSetData(reconstructedData);

          } else {
            throw new Error("API response did not contain the expected endorsementDetails structure.");
          }
        })
        .catch((error) => {
          console.error("Failed to fetch or process endorsement details:", error);
          const errorMessage = typeof error === 'string' ? error : (error.message || "Failed to fetch details.");
          message.error(errorMessage);
          navigate("/admin/endorsement_list");
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      message.error("No Endorsement ID found. Please navigate from the list. Redirecting...");
      setLoading(false);
      navigate("/admin/endorsement_list");
    }
  }, [location.state, dispatch, navigate]);

  const handleDownloadFile = async () => {
    const uploadId = endorsementData?.ref_endorsement_calculation_data_upload_id;
    
    if (!uploadId) {
      message.error("Uploaded file ID not found in the endorsement data.");
      return;
    }

    setIsDownloading(true);
    try {
      const response = await dispatch(getEndorsementCalculationUploadedFile(uploadId));
      const fileLink = response?.endorsementDetails;

      if (fileLink && typeof fileLink === 'string') {
        const link = document.createElement('a');
        link.href = fileLink;
        link.setAttribute('download', fileLink.substring(fileLink.lastIndexOf('/') + 1) || 'uploaded-file.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        link.remove();
      } else {
        throw new Error("Invalid file link received from server.");
      }
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error : (error.message || "Failed to download the file.");
      console.error("Download failed:", error);
      message.error(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  // --- MODIFICATION: Updated handler to match the API response ---
  const handleExport = async () => {
    const endorsementId = location.state?.endorsementId;
    if (!endorsementId) {
        message.error("Endorsement ID is missing. Cannot export.");
        return;
    }

    setIsExporting(true);
    try {
        const response = await dispatch(getEndorsementCalculationDataExport(endorsementId));
        
        // --- FIX: Changed from `response.url` to `response.download_link` to match your API action ---
        const fileUrl = response?.download_link;

        if (fileUrl && typeof fileUrl === 'string') {
            const link = document.createElement('a');
            link.href = fileUrl;
            const fileName = `Export-Data-${endorsementData?.endorsement_number || endorsementId}.csv`;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            message.success("Data export has started.");
        } else {
            // This error will now be correctly thrown if `download_link` is missing from the API response
            throw new Error("Invalid or missing file URL in the API response for export.");
        }
    } catch (error) {
        const errorMessage = typeof error === 'string' ? error : (error.message || "An unknown error occurred during export.");
        console.error("Export failed:", error);
        message.error(errorMessage);
    } finally {
        setIsExporting(false);
    }
  };


  if (loading) {
    return <Spin size="large" style={{ display: 'block', marginTop: '50px' }} />;
  }

  if (!endorsementData) {
    return <Card><p>No data found for this endorsement. You may be redirected.</p></Card>;
  }

  const cardHeader = (
    <Row justify="space-between" align="middle">
      <Col>
        <Title level={5} style={{ margin: 0 }}>Endorsement Calculation Details</Title>
      </Col>
      <Col>
        <Space>
          <Button 
            type="primary" 
            icon={<ExportOutlined />} 
            style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
            onClick={handleExport}
            loading={isExporting}
          >
            Export
          </Button>
          <Button
            style={{ backgroundColor: '#6c757d', color: 'white', borderColor: '#6c757d' }}
            onClick={handleDownloadFile}
            loading={isDownloading}
          >
            Download Uploaded File
          </Button>
        </Space>
      </Col>
    </Row>
  );

  return (
    <Card>
      <div className="card-body">
        <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
          <Col>
            <Title level={4} style={{ margin: 0 }}>{endorsementData.endorsement_title}</Title>
          </Col>
          <Col>
            <Button onClick={() => navigate(-1)}>Back to List</Button>
          </Col>
        </Row>
        <Divider />
        <Form layout="vertical" form={form}>
            <Row gutter={16}>
                <Col xs={24} sm={12} md={6}><Form.Item name="corporate_name" label="Corporate"><Input disabled /></Form.Item></Col>
                <Col xs={24} sm={12} md={6}><Form.Item name="policy_number" label="Policy No"><Input disabled /></Form.Item></Col>
            </Row>
            <Row gutter={16}>
                <Col xs={24} sm={12} md={6}><Form.Item label="Policy Start Date" name="policy_start_date"><Input disabled /></Form.Item></Col>
                <Col xs={24} sm={12} md={6}><Form.Item label="Policy End Date" name="policy_end_date"><Input disabled /></Form.Item></Col>
                <Col xs={24} sm={12} md={6}><Form.Item label="Policy Type" name="policy_type"><Input disabled /></Form.Item></Col>
                <Col xs={24} sm={12} md={6}><Form.Item label="Enrollment Type" name="enrollment_type"><Input disabled /></Form.Item></Col>
            </Row>
            <Divider />
            
            <Row>
              <Col span={24}>
                <Card type="inner" title={cardHeader}>
                  <Collapse className="custom-collapse" style={{ marginBottom: "20px" }} defaultActiveKey={["1"]} items={[{ key: "1", label: "Addition Calculation", children: (<Table columns={additionColumns} dataSource={additionTableData} pagination={false} rowKey="key" />)}]}/>
                  <Collapse className="custom-collapse-del" defaultActiveKey={["2"]} items={[{ key: "2", label: "Deletion Calculation", children: (<Table columns={deletionColumns} dataSource={deletionTableData} pagination={false} rowKey="key" />)}]}/>
                </Card>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: '24px' }}>
              <Col xs={24} sm={8}><Form.Item label="Total Addition Premium" name="total_addition_premium"><Input disabled style={{ fontWeight: 'bold' }} /></Form.Item></Col>
              <Col xs={24} sm={8}><Form.Item label="Total Deletion Premium" name="total_deletion_premium"><Input disabled style={{ fontWeight: 'bold' }} /></Form.Item></Col>
              <Col xs={24} sm={8}><Form.Item label="Net Premium" name="net_premium"><Input disabled style={{ fontWeight: 'bold', color: 'green' }} /></Form.Item></Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={8}><Form.Item label="Endorsement Title" name="endorsement_title"><Input disabled /></Form.Item></Col>
              <Col xs={24} sm={8}><Form.Item label="Endorsement Number" name="endorsement_number"><Input disabled /></Form.Item></Col>
              <Col xs={24} sm={8}><Form.Item label="Transaction Statement" name="transaction_statements"><Input.TextArea disabled autoSize={{ minRows: 1 }} /></Form.Item></Col>
            </Row>
        </Form>
      </div>
    </Card>
  );
};

export default ViewEndorsement;